import { isAfter, subDays } from 'date-fns'
import { Capacitor } from '@capacitor/core'

import Api from 'services/Api'
import { hashTable, hasAccessToThis } from 'services/Utilities'
import { createErrorNotification } from 'store/actions/notifications'
import { getSocket } from 'store/middleware/realtime'
import { invalidateQueries } from 'Api'

// Lists of the endpoints to load data from
const resourcesEndpoint = {
  contactConfigs: `contact/configs`,
  departments: `departments/departments`,
  hiringData: 'users/hiringData', //?fields={"name":1,"type":1}',
  wagePremiums: `pay/wagePremiums`,
  permissions: `permissions/permissions`,
  projects: `projects/projects`,
  projectOwners: `projects/owners`,
  punchRules: `punch/rules`,
  requestFields: `requests/fields`,
  scheduleTypes: `schedule/types`,
  scheduleTemplates: `schedule/template`,
  structures: `structure/structure`, //?fields={"name":1}`,
  tags: `tags/tags`, //?fields={"name":1,"type":1,"address":1}`,
  teams: `teams/teams`,
  holidays: `holidays/holidays`,
  userFilters: `users/filters`,
  userProcedures: `users/procedures`,
  userSorts: `users/sorts`,
  users: 'users/users?showArchived=true', //?fields={"firstName":1,"lastName":1,"picture":1}'
  userTemplates: 'users/templates',
  contactTemplates: 'contact/templates'
}

const merinioAccounts = [
  { _id: '000000000000000000000000', fullName: 'Merinio - Admin', firstName: 'Merinio - Admin', lastName: '' },
  { _id: 'aaaaaaaaaaaaaaaaaaaaaaaa', fullName: 'API Integration', firstName: 'API Integration', lastName: '' },
  { _id: 'Merinio', fullName: 'Merinio', firstName: 'Merinio' }
]

const setResourcesAllLoaded = () => ({ type: 'SET_RESOURCES_ALL_LOADED' })
export const setNoInternet = noInternet => ({ type: 'SET_INTERNET_STATE', noInternet })

// Function used to set some value into the application state
const setValues = (values, type, name) => ({
  type,
  [name]: values,
  [`${name}ById`]: hashTable(values),
  [`${name}Ids`]: values.map(({ _id }) => _id)
})
const setHiringData = hiringData => ({
  type: 'SET_HIRING_DATA',
  hiringData,
  hiringDataById: hashTable(hiringData),
  hiringDataIds: hiringData.map(({ _id }) => _id)
})
const setStructures = positions => ({
  type: 'SET_STRUCTURES',
  positions,
  positionsById: hashTable(positions),
  structureIds: positions.map(({ _id }) => _id)
})
const setTags = tags => ({ type: 'SET_TAGS', tagsById: hashTable(tags), tagsIds: tags.map(({ _id }) => _id), tags })
const setUsers = users => ({ type: 'SET_USERS', usersById: hashTable([...users, ...merinioAccounts]), usersIds: users.map(({ _id }) => _id), users })

const setRequestFields = requestField => ({
  type: 'SET_REQUESTS_FIELDS',
  requestStatus: requestField.status,
  requestStatusById: hashTable(requestField.status, 'id'),
  vacationTypesById: hashTable(requestField.vacationType),
  attendanceTypesById: hashTable(requestField.leaveType, 'id'),
  attendanceTypes: requestField.leaveType,
  timeOffTypes: requestField.vacationType,
  timeOffTypesById: hashTable(requestField.vacationType),
  attendanceReasonsById: hashTable([...requestField.leaveReason, ...requestField.lateReason, ...requestField.earlyOutReason]),
  attendanceReasons: [...requestField.leaveReason, ...requestField.lateReason, ...requestField.earlyOutReason]
})

const setDepartments = departments => ({
  type: 'SET_DEPARTMENTS',
  departments,
  departmentsById: hashTable(departments),
  departmentsIds: departments.map(({ _id }) => _id)
})

/**
 * Will queue in a Set the resource to refresh, the set will then be cleared after 1s of waiting
 * @param {*} dispatch
 * @param {*} message message from RT saying which resources have changed
 */
export const triggerResourcesRefresh = (dispatch, { resource = [] } = {}) => resource.map(r => fetchResourceUpdate(dispatch, r))

/**
 * Function to update all stale resources, normally runs on page change
 */
export function refreshStaleResources() {
  return (dispatch, getStore) => {
    const {
      resources: { staleResources }
    } = getStore()

    const resource = Object.keys(staleResources)
    if (!resource.length) return

    dispatch({ type: 'CLEAR_STALE_RESOURCES' })
    triggerResourcesRefresh(dispatch, { resource: resource })
  }
}

// Action to refresh a specific resources when it was changed in the backend (resourceNotifierService)
async function fetchResourceUpdate(dispatch, resource) {
  switch (resource) {
    case 'contactConfigs': {
      const { result, success } = await Api(resourcesEndpoint.contactConfigs)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_CONFIGS', 'configs'))
    }
    case 'departments': {
      const { departments, success } = await Api(resourcesEndpoint.departments)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setDepartments(departments))
    }
    case 'hiringDatas': {
      const res = await Api(resourcesEndpoint.hiringData)
      if (!Array.isArray(res)) return dispatch(createErrorNotification())
      return dispatch(setHiringData(res))
    }
    case 'wagePremiums': {
      const { result, success } = await Api(resourcesEndpoint.wagePremiums)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_WAGE_PREMIUMS', 'wagePremiums'))
    }
    case 'punchRules': {
      const { result, success } = await Api(resourcesEndpoint.punchRules)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_PUNCH_RULES', 'punchRules'))
    }
    case 'projects': {
      if (!hasAccessToThis('projectSee')) return
      const { projects, success } = await Api(resourcesEndpoint.projects)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(projects, 'SET_PROJECTS', 'projects'))
    }
    case 'projectOwners': {
      if (!hasAccessToThis('projectSee')) return
      const { projectOwners, success } = await Api(resourcesEndpoint.projectOwners)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(projectOwners, 'SET_PROJECT_OWNERS', 'projectOwners'))
    }
    case 'permissions': {
      const { result, success } = await Api(resourcesEndpoint.permissions)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_PERMISSIONS', 'permissions'))
    }
    case 'requestsFields': {
      const { success, ...rest } = await Api(resourcesEndpoint.requestFields)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setRequestFields(rest))
    }
    case 'scheduleTypes': {
      const { type, success } = await Api(resourcesEndpoint.scheduleTypes)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(type, 'SET_SCHEDULE_TYPES', 'scheduleTypes'))
    }
    case 'scheduleTemplates': {
      const { result, success } = await Api.get(resourcesEndpoint.scheduleTemplates, { params: { useNewReturn: true } })
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_SCHEDULE_TEMPLATES', 'scheduleTemplates'))
    }
    case 'structures': {
      const res = await Api(resourcesEndpoint.structures)
      if (!Array.isArray(res)) return dispatch(createErrorNotification())
      return dispatch(setStructures(res))
    }
    case 'tags': {
      const { tags, success } = await Api(resourcesEndpoint.tags)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setTags(tags))
    }
    case 'teams': {
      const { teams, success } = await Api(resourcesEndpoint.teams)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(teams, 'SET_TEAMS', 'teams'))
    }
    case 'holidays': {
      const { result, success } = await Api(resourcesEndpoint.holidays)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_HOLIDAYS', 'holidays'))
    }
    case 'userFilters': {
      const { result, success } = await Api(resourcesEndpoint.userFilters)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_FILTERS', 'filters'))
    }
    case 'userProcedures': {
      const { result, success } = await Api(resourcesEndpoint.userProcedures)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_PROCEDURES', 'procedures'))
    }
    case 'userSorts': {
      const { result, success } = await Api(resourcesEndpoint.userSorts)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_SORTS', 'sorts'))
    }
    case 'users': {
      const { users, success } = await Api(resourcesEndpoint.users)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setUsers(users))
    }

    case 'userTemplates': {
      if (!hasAccessToThis('userTemplateSee')) return
      const { result, success } = await Api(resourcesEndpoint.userTemplates)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(result, 'SET_USER_TEMPLATES', 'userTemplates'))
    }
    case 'contactTemplates': {
      if (!hasAccessToThis('contactNotifications')) return
      const { templates, success } = await Api(resourcesEndpoint.contactTemplates)
      if (!success) return dispatch(createErrorNotification())
      return dispatch(setValues(templates, 'SET_CONTACT_TEMPLATES', 'contactTemplates'))
    }

    default:
      return
  }
}

// Function to query the user's informations
export const fetchResourcesInformation = () => {
  return async dispatch => {
    dispatch({ type: 'SET_IS_LOADING_RESOURCES' })
    const Socket = getSocket()

    // Watch for resource modifications // Will be removed when login out (in actions/auth.js)
    // Turn off the listener first, JUST so it will never be added twice.
    Socket.off('removeFromResources')
    Socket.on('removeFromResources', message => {
      // invalidate react queries

      invalidateQueries(message.resource)
      dispatch({ type: 'SET_STALE_RESOURCES', resources: message.resource })
    })

    // Fetches all the resources
    const [
      contactConfigs,
      contactTemplates,
      departments,
      hiringData,
      holidays,
      wagePremiums,
      punchRules,
      requestFields,
      scheduleTypes,
      structures,
      tags,
      teams,
      userFilters,
      userProcedures,
      userSorts,
      users,
      scheduleTemplates,
      userTemplates,
      permissions,
      projects,
      projectOwners
    ] = await Promise.all([
      Api(resourcesEndpoint.contactConfigs),
      window.Cypress || hasAccessToThis('contactNotifications') ? Api(resourcesEndpoint.contactTemplates) : null,
      Api(resourcesEndpoint.departments),
      Api(resourcesEndpoint.hiringData),
      Api(resourcesEndpoint.holidays),
      Api(resourcesEndpoint.wagePremiums),
      Api(resourcesEndpoint.punchRules),
      Api(resourcesEndpoint.requestFields),
      Api(resourcesEndpoint.scheduleTypes),
      Api(resourcesEndpoint.structures),
      Api(resourcesEndpoint.tags),
      Api(resourcesEndpoint.teams),
      Api(resourcesEndpoint.userFilters),
      Api(resourcesEndpoint.userProcedures),
      Api(resourcesEndpoint.userSorts),
      Api(`users/users?showArchived=true`), // Load all users the first time
      window.Cypress || hasAccessToThis('scheduleTemplateSee') ? Api(resourcesEndpoint.scheduleTemplates, { params: { useNewReturn: true } }) : null,
      window.Cypress || hasAccessToThis('userTemplateSee') ? Api(resourcesEndpoint.userTemplates) : null,
      Api(resourcesEndpoint.permissions),
      window.Cypress || hasAccessToThis('projectSee') ? Api(resourcesEndpoint.projects) : null,
      window.Cypress || hasAccessToThis('projectSee') ? Api(resourcesEndpoint.projectOwners) : null
    ])

    // TODO: handle API error
    dispatch(setValues(contactConfigs.result, 'SET_CONFIGS', 'configs'))
    if (window.Cypress || hasAccessToThis('contactNotifications')) dispatch(setValues(contactTemplates.templates, 'SET_CONTACT_TEMPLATES', 'contactTemplates'))
    dispatch(setDepartments(departments.departments))
    dispatch(setHiringData(hiringData))
    dispatch(setValues(wagePremiums.result, 'SET_WAGE_PREMIUMS', 'wagePremiums'))
    dispatch(setValues(punchRules.result, 'SET_PUNCH_RULES', 'punchRules'))
    dispatch(setRequestFields(requestFields))
    dispatch(setValues(scheduleTypes.type, 'SET_SCHEDULE_TYPES', 'scheduleTypes'))
    dispatch(setStructures(structures))
    dispatch(setTags(tags.tags))
    dispatch(setValues(teams.teams, 'SET_TEAMS', 'teams'))
    dispatch(setValues(holidays.result, 'SET_HOLIDAYS', 'holidays'))
    dispatch(setValues(userFilters.result, 'SET_FILTERS', 'filters'))
    dispatch(setValues(userProcedures.result, 'SET_PROCEDURES', 'procedures'))
    dispatch(setValues(userSorts.result, 'SET_SORTS', 'sorts'))
    dispatch(setUsers(users.users))
    dispatch(setValues(permissions.result, 'SET_PERMISSIONS', 'permissions'))
    if (window.Cypress || hasAccessToThis('scheduleTemplateSee')) dispatch(setValues(scheduleTemplates.result, 'SET_SCHEDULE_TEMPLATES', 'scheduleTemplates'))
    if (window.Cypress || hasAccessToThis('userTemplateSee')) dispatch(setValues(userTemplates.result, 'SET_USER_TEMPLATES', 'userTemplates'))
    if (window.Cypress || hasAccessToThis('projectSee')) dispatch(setValues(projects.projects, 'SET_PROJECTS', 'projects'))
    if (window.Cypress || hasAccessToThis('projectSee')) dispatch(setValues(projectOwners.projectOwners, 'SET_PROJECT_OWNERS', 'projectOwners'))
    // Everything is loaded
    dispatch(setResourcesAllLoaded())
    dispatch({ type: 'REMOVE_IS_LOADING_RESOURCES' })
  }
}

// Resources should be refetched after a while (the app on some device will never be killed, this means that the resources could be cached for eternity)
// This will make sure that the resources are never older then a day old (1 day is an arbritary initial value)
export const checkIfResourcesRefreshIsNeeded = () => {
  return async (dispatch, getStore) => {
    // If we are in the browser, skip
    if (!Capacitor.isNativePlatform()) return

    // Get the lastRefreshedAt from the store
    const {
      resources: { lastRefreshedAt }
    } = getStore()

    // Resources have not been fetched yet, skip
    if (!lastRefreshedAt) return

    const nowLessOneDay = subDays(new Date(), 1)
    if (isAfter(nowLessOneDay, lastRefreshedAt)) {
      Object.keys(resourcesEndpoint).map(r => fetchResourceUpdate(dispatch, r))
      dispatch({ type: 'SET_IS_REFRESHING_RESOURCES' })
    }
  }
}
